// ./react-app/src/components/DailyControllingCustomerFilter.tsx
import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { tokens } from '../theme';

export interface CustomerOption {
  value: string;
  label: string;
  color: string;
}

interface DailyControllingCustomerFilterProps {
  selectedCustomers: string[];
  onSelectionChange: (customers: string[]) => void;
}

export const customerOptions: CustomerOption[] = [
  { value: 'ft', label: 'Finanztrends', color: '#2563eb' },
  { value: 'bg', label: 'Börse Global', color: '#10b981' },
  { value: 'sw', label: 'Stock-World', color: '#f59e0b' },
  { value: 'tt', label: 'Trading-Treff', color: '#ef4444' },
  { value: 'be', label: 'Börse-Express', color: '#8b5cf6' },
  { value: 'foreign', label: 'Foreign Leads', color: '#6b7280' },
  { value: 'non_finance', label: 'Non-Finance', color: '#ec4899' },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const DailyControllingCustomerFilter: React.FC<DailyControllingCustomerFilterProps> = ({
  selectedCustomers,
  onSelectionChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onSelectionChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customerOptions.length) {
      // If all are selected, deselect all
      onSelectionChange([]);
    } else {
      // If not all are selected, select all
      onSelectionChange(customerOptions.map((option) => option.value));
    }
  };

  const isAllSelected = selectedCustomers.length === customerOptions.length;
  const isIndeterminate =
    selectedCustomers.length > 0 && selectedCustomers.length < customerOptions.length;

  return (
    <Box sx={{ minWidth: 300, maxWidth: 400 }}>
      <FormControl fullWidth>
        <InputLabel id="customer-multi-select-label">Kunden</InputLabel>
        <Select
          labelId="customer-multi-select-label"
          id="customer-multi-select"
          multiple
          value={selectedCustomers}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label="Kunden" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => {
                const option = customerOptions.find((opt) => opt.value === value);
                return (
                  <Chip
                    key={value}
                    label={option?.label || value}
                    size="small"
                    sx={{
                      backgroundColor: option?.color,
                      color: 'white',
                      fontWeight: 'bold',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
          MenuProps={MenuProps}
          sx={{
            '& .MuiSelect-select': {
              minHeight: '56px',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {/* Select All Option */}
          <MenuItem onClick={handleSelectAll} sx={{ fontWeight: 'bold' }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              sx={{
                color: colors.blueAccent[600],
                '&.Mui-checked': {
                  color: colors.blueAccent[600],
                },
                '&.MuiCheckbox-indeterminate': {
                  color: colors.blueAccent[600],
                },
              }}
            />
            <ListItemText primary={isAllSelected ? 'Alle abwählen' : 'Alle auswählen'} />
          </MenuItem>

          {/* Individual Customer Options */}
          {customerOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                checked={selectedCustomers.includes(option.value)}
                sx={{
                  color: option.color,
                  '&.Mui-checked': {
                    color: option.color,
                  },
                }}
              />
              <ListItemText primary={option.label} />
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: option.color,
                  borderRadius: '50%',
                  marginLeft: 1,
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Summary text */}
      <Box sx={{ mt: 1, fontSize: '0.875rem', color: colors.grey[100] }}>
        {selectedCustomers.length === 0
          ? 'Keine Kunden ausgewählt'
          : selectedCustomers.length === customerOptions.length
            ? 'Alle Kunden ausgewählt'
            : `${selectedCustomers.length} von ${customerOptions.length} Kunden ausgewählt`}
      </Box>
    </Box>
  );
};

export default DailyControllingCustomerFilter;
